import React, { useCallback } from "react";
import { CalendarWrap } from "./CalendarUI";
import Month from "./Month";
import Day from "./Day";
import Event from "./Event";
import { Venue } from "../../../typings/document-types";
import { ShowSearchResult, ShowTime, EventInfo, SalesStop } from "../../../typings/typings";
import { format, getMonth, getYear } from "../../../lib/date";
import getDate from "../../../lib/date/getDate";

interface CalendarProps
{
    buyOnClick: (eventNo: number, showNo?: number) => void;
    shows: ShowSearchResult[];
    maxEvents?: number;
}

interface ShowEvent
{
    date: Date;
    name: string;
    location: string;
    eventNo: number;
    notes: string;
    showNo: number;
    soldOut: boolean;
    to: string;
    salesStop: SalesStop;
    purchaseUrls: any;
}

const Calendar: React.FC<CalendarProps> = (props) =>
{
    const { shows } = props;

    const getShowsEvents = useCallback(() =>
    {
        const getVenueNames = (venues: Venue[] | undefined): string =>
        {
            if (venues)
            {
                return venues.map((venue) =>
                {
                    return venue.name;
                }).join(" + ");
            }
            return "";
        };

        const showEvents = shows.reduce((events: ShowEvent[], show: ShowSearchResult) =>
        {
            if (show.events)
            {
                show.events?.forEach((event: EventInfo) =>
                {
                    if (event.showTimes)
                    {
                        event.showTimes.forEach((showTime: ShowTime) =>
                        {
                            const e: ShowEvent = {
                                date: showTime.date,
                                showNo: showTime.showNo as number,
                                eventNo: event.eventNo as number,
                                name: show.name,
                                notes: showTime.notes,
                                to: show.url,
                                location: getVenueNames(show.venues),
                                soldOut: showTime.isSoldOut,
                                salesStop: showTime.salesStop,
                                purchaseUrls: showTime.purchaseUrls
                            };
                            events.push(e);

                        });
                    }
                });
            }
            const sortedEvents = events.sort((a, b) => a.date.getTime() - b.date.getTime());
            return sortedEvents;
        }, []);

        // Should be sorted asc already, but lets ensure
        const sorted = showEvents.sort((left, right) => left.date.getTime() - right.date.getTime());
        return sorted;
    }, [shows]);

    const createEvent = (showEvent: ShowEvent, index: number) =>
        (
            <Event key={`${index}_${showEvent.eventNo}_${showEvent.showNo}`}
                name={showEvent.name}
                to={showEvent.to}
                time={`${format(showEvent.date, "HH:mm")}`}
                location={showEvent.location}
                eventNo={showEvent.eventNo}
                showNo={showEvent.showNo}
                notes={showEvent.notes}
                soldOut={showEvent.soldOut}
                buyOnClick={props.buyOnClick}
                salesStop={showEvent.salesStop}
                purchaseUrls={showEvent.purchaseUrls}
            >
                {/* Tilkøb muligt */}
                {/* <Button onClick={() => setEventTooltipOn(!eventTooltipOn)}>
                        <span className="tooltip__i h-4 w-4 flex items-center justify-center ml-2 mb-2 text-small lowercase">i</span>
                    </Button>
                    {
                        (eventTooltipOn) ? (
                            <>
                                <Tooltip
                                    open={eventTooltipOn}
                                    setOpen={() => setEventTooltipOn(!eventTooltipOn)}>
                                    <p>Skate ipsum dolor sit amet, alley oop vert mute-air Colby Carter flail 180 berm.</p>
                                </Tooltip>
                            </>
                        ) : null
                    } */}
            </Event>
        );

    const createMonth = (date: Date, days: ReturnType<typeof Day>[]) =>
        (
            <Month key={`${date.getFullYear()}_${getMonth(date)}`} name={format(date, "MMM")} year={getYear(date)}>
                {days}
            </Month>
        );

    const createDay = (date: Date, eventsOnDay: ReturnType<typeof Event>[]) =>
        (
            <Day key={`${date.getFullYear()}_${getMonth(date)}_${getDate(date)}`} number={getDate(date)} name={format(date, "cccc")} month={format(date, "MMM")}>
                {eventsOnDay}
            </Day>
        );

    const createMonths = (sE: ShowEvent[]) =>
    {
        const showEvents = props.maxEvents ? sE.slice(0, props.maxEvents) : sE;
        const months: ReturnType<typeof Month>[] = [];
        let index = 0;
        // Loop months
        let prevMonth: number | undefined = undefined;
        while (showEvents[index])
        {
            const currMonth: number = getMonth(showEvents[index].date);
            const currYear = getYear(showEvents[index].date);
            let currDayOfMonth = getDate(showEvents[index].date);

            prevMonth = prevMonth ? prevMonth : currMonth;

            const daysInMonth: ReturnType<typeof Day>[] = [];

            // Loop days, while same month
            while (showEvents[index] && getMonth(showEvents[index].date) === currMonth)
            {
                const eventsOnDay: ReturnType<typeof Event>[] = [];
                currDayOfMonth = getDate(showEvents[index].date);

                // Loop events, multiple on same day
                while (showEvents[index] && getDate(showEvents[index].date) === currDayOfMonth)
                {
                    const showEvent = showEvents[index];
                    eventsOnDay.push(createEvent(showEvent, index));
                    index++; // Success
                }

                daysInMonth.push(createDay(new Date(currYear, currMonth, currDayOfMonth), eventsOnDay));
            }

            months.push(createMonth(new Date(currYear, currMonth, currDayOfMonth), daysInMonth));
        }

        return months;
    };

    const showEvents = getShowsEvents();

    return (
        <CalendarWrap>
            {showEvents.length > 0 ?
                createMonths(showEvents) : null}
        </CalendarWrap>
    );
};

export default Calendar;